import { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Grid, CircularProgress, Alert,
  Button, Accordion, AccordionSummary, AccordionDetails, Link, Tooltip, Avatar,
} from '@mui/material';
import {
  ExpandMore, Refresh, Radar, RocketLaunch, Visibility, Bolt, TrendingUpOutlined,
  LinkedIn, OpenInNew, Star,
} from '@mui/icons-material';
import apiService from '../services/api';

const ACCENT = '#3B82F6';      // Israeli-blue
const ACCENT2 = '#1D4ED8';

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  const days = Math.floor((Date.now() - d) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function HeadlineLink({ headline, url, source, accent }: { headline: string; url?: string | null; source?: string | null; accent: string }) {
  return (
    <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.45, display: 'block', mt: 0.5 }}>
      {url ? (
        <Link href={url} target="_blank" rel="noopener noreferrer"
          sx={{ color: 'text.secondary', textDecorationColor: `${accent}66`, '&:hover': { color: accent } }}>
          {headline}
        </Link>
      ) : <span style={{ opacity: 0.85 }}>{headline}</span>}
      {source && <span style={{ opacity: 0.5 }}> — {source}</span>}
    </Typography>
  );
}

// ── Discovery: funding / stealth / founder-moves ──────────────────────────────
function DiscoverySection({ discovery, onRefresh, scanning }: { discovery: any; onRefresh: () => void; scanning: boolean }) {
  const [open, setOpen] = useState(true);
  if (!discovery) return null;
  const funding = discovery.funding_rounds ?? [];
  const stealth = discovery.stealth_launches ?? [];
  const founders = discovery.founder_moves ?? [];
  const scanned = discovery.articles_scanned ?? 0;

  return (
    <Accordion expanded={open} onChange={() => setOpen(o => !o)}
      sx={{ mb: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: open ? ACCENT : 'divider',
        borderRadius: '8px !important', '&:before': { display: 'none' }, boxShadow: open ? `0 0 20px ${ACCENT}22` : 'none' }}>
      <AccordionSummary expandIcon={<ExpandMore sx={{ color: ACCENT }} />} sx={{ borderRadius: open ? '8px 8px 0 0' : '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
          <Radar sx={{ color: ACCENT, fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography fontWeight="bold" sx={{ color: open ? ACCENT : 'text.primary' }}>Live Deal Radar</Typography>
              {funding.length > 0 && <Chip label={`${funding.length} rounds`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: `${ACCENT}22`, color: ACCENT }} />}
              {stealth.length > 0 && <Chip label={`${stealth.length} stealth`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#7C3AED22', color: '#A78BFA' }} />}
              {founders.length > 0 && <Chip label={`${founders.length} founder moves`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#F59E0B22', color: '#FBBF24' }} />}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Mined from {scanned.toLocaleString()} Israeli tech-news articles · {discovery.scan_period_days}d window
            </Typography>
          </Box>
          <Button size="small" variant="outlined" startIcon={scanning ? <CircularProgress size={12} color="inherit" /> : <Refresh />}
            onClick={e => { e.stopPropagation(); onRefresh(); }} disabled={scanning}
            sx={{ color: ACCENT, borderColor: `${ACCENT}55`, fontSize: '0.7rem', py: 0.3 }}>
            {scanning ? 'Scanning…' : 'Re-scan'}
          </Button>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: { xs: 1.5, md: 2.5 }, pt: 1 }}>
        <Grid container spacing={2.5}>
          {/* Funding rounds */}
          <Grid item xs={12} md={5}>
            <Typography variant="subtitle2" fontWeight="bold" color={ACCENT} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUpOutlined sx={{ fontSize: 16 }} /> Fresh Funding Rounds
            </Typography>
            {funding.length === 0 ? <Typography variant="body2" color="text.disabled">No rounds detected.</Typography> : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {funding.map((f: any, i: number) => (
                  <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: `${ACCENT}33`, bgcolor: `${ACCENT}08` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight="bold">{f.company}</Typography>
                        {f.known && <Chip size="small" label="known" sx={{ height: 15, fontSize: '0.55rem', bgcolor: '#ffffff18', color: 'text.secondary' }} />}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        {f.amount && <Chip size="small" label={f.amount} sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#10B98122', color: '#34D399' }} />}
                        {f.stage && <Chip size="small" label={f.stage} sx={{ height: 16, fontSize: '0.58rem', bgcolor: `${ACCENT}22`, color: ACCENT }} />}
                      </Box>
                    </Box>
                    <HeadlineLink headline={f.latest_headline} url={f.latest_url} source={f.latest_source} accent={ACCENT} />
                    {f.published_at && <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>{timeAgo(f.published_at)}</Typography>}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Stealth launches */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" fontWeight="bold" color="#A78BFA" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Visibility sx={{ fontSize: 16 }} /> Out of Stealth
            </Typography>
            {stealth.length === 0 ? <Typography variant="body2" color="text.disabled">No stealth launches.</Typography> : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {stealth.map((s: any, i: number) => (
                  <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: '#7C3AED33', bgcolor: '#7C3AED08' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      {s.amount && <Chip size="small" label={s.amount} sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#10B98122', color: '#34D399' }} />}
                    </Box>
                    <HeadlineLink headline={s.headline} url={s.url} source={s.source} accent="#A78BFA" />
                    {s.published_at && <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>{timeAgo(s.published_at)}</Typography>}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Founder moves */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" fontWeight="bold" color="#FBBF24" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Bolt sx={{ fontSize: 16 }} /> Founder Moves
            </Typography>
            {founders.length === 0 ? <Typography variant="body2" color="text.disabled">No founder signals.</Typography> : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {founders.map((m: any, i: number) => (
                  <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: '#F59E0B33', bgcolor: '#F59E0B08' }}>
                    <Chip size="small" label={m.pedigree} sx={{ height: 16, fontSize: '0.58rem', bgcolor: '#F59E0B22', color: '#FBBF24', mb: 0.3 }} />
                    <HeadlineLink headline={m.headline} url={m.url} source={m.source} accent="#FBBF24" />
                  </Box>
                ))}
              </Box>
            )}
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
          Auto-detected from live Israeli tech-news (Calcalist/CTech, Globes, Geektime, NoCamels via Google News).
          Each funding signal leads a headline with a verified amount. Research starting point — verify before investing.
        </Typography>
      </AccordionDetails>
    </Accordion>
  );
}

// ── Founders to watch ─────────────────────────────────────────────────────────
function FounderCard({ f }: { f: any }) {
  const buzz = f.news_buzz?.mentions_30d ?? 0;
  const initials = f.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('');
  return (
    <Card sx={{ height: '100%', border: '1px solid', borderColor: buzz > 0 ? `${ACCENT}55` : 'divider', bgcolor: 'background.paper' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
          <Avatar sx={{ bgcolor: ACCENT2, width: 40, height: 40, fontSize: '0.9rem', fontWeight: 'bold' }}>{initials}</Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, justifyContent: 'space-between' }}>
              <Typography variant="subtitle2" fontWeight="bold" noWrap>{f.name}</Typography>
              {buzz > 0 && (
                <Tooltip title={`${buzz} news mentions in last 30 days`}>
                  <Chip size="small" icon={<Bolt sx={{ fontSize: '11px !important', color: '#FBBF24 !important' }} />} label={buzz}
                    sx={{ height: 18, fontSize: '0.62rem', bgcolor: '#F59E0B22', color: '#FBBF24' }} />
                </Tooltip>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{f.current}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {f.unit && f.unit !== '—' && <Chip size="small" label={f.unit} sx={{ height: 17, fontSize: '0.58rem', bgcolor: `${ACCENT}1a`, color: ACCENT }} />}
          {(f.tags || []).slice(0, 3).map((t: string) => (
            <Chip key={t} size="small" label={t} sx={{ height: 17, fontSize: '0.56rem', bgcolor: '#ffffff12', color: 'text.secondary' }} />
          ))}
        </Box>
        <Box sx={{ bgcolor: '#10B98111', borderLeft: '3px solid #10B981', pl: 1, py: 0.5, mb: 1, borderRadius: '0 4px 4px 0' }}>
          <Typography variant="caption" fontWeight="bold" color="#34D399" sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Star sx={{ fontSize: 11 }} /> Exits: {f.exits}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.45, display: 'block', mb: 0.5 }}>
          <strong>Watch for:</strong> {f.watch_for}
        </Typography>
        {f.news_buzz?.latest_headline && (
          <HeadlineLink headline={f.news_buzz.latest_headline} accent={ACCENT} />
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button size="small" startIcon={<LinkedIn sx={{ fontSize: 15 }} />} href={f.linkedin} target="_blank" rel="noopener noreferrer"
            sx={{ fontSize: '0.65rem', textTransform: 'none', color: ACCENT, p: 0.2, minWidth: 0 }}>LinkedIn</Button>
          <Button size="small" startIcon={<OpenInNew sx={{ fontSize: 13 }} />} href={f.x_search} target="_blank" rel="noopener noreferrer"
            sx={{ fontSize: '0.65rem', textTransform: 'none', color: 'text.secondary', p: 0.2, minWidth: 0 }}>X / search</Button>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── Sector accordion ──────────────────────────────────────────────────────────
function SectorAccordion({ sec, expanded, onToggle }: { sec: any; expanded: boolean; onToggle: () => void }) {
  return (
    <Accordion expanded={expanded} onChange={onToggle}
      sx={{ mb: 1.5, bgcolor: 'background.paper', border: '1px solid', borderColor: expanded ? sec.color : 'divider',
        borderRadius: '8px !important', '&:before': { display: 'none' }, boxShadow: expanded ? `0 0 16px ${sec.color}22` : 'none' }}>
      <AccordionSummary expandIcon={<ExpandMore sx={{ color: sec.color }} />} sx={{ borderRadius: expanded ? '8px 8px 0 0' : '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <Typography sx={{ fontSize: '1.3rem' }}>{sec.emoji}</Typography>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography fontWeight="bold" sx={{ color: expanded ? sec.color : 'text.primary' }}>{sec.name}</Typography>
              <Chip label={`${sec.startups.length}`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: `${sec.color}22`, color: sec.color }} />
            </Box>
            {!expanded && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{sec.thesis.slice(0, 90)}…</Typography>}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: { xs: 1.5, md: 2.5 }, pt: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, p: 1.5, bgcolor: `${sec.color}0a`, borderRadius: 2 }}>
          {sec.thesis}
        </Typography>
        <Grid container spacing={1.5}>
          {sec.startups.map((s: any) => {
            const buzz = s.news_buzz?.mentions_30d ?? 0;
            return (
              <Grid item xs={12} sm={6} md={4} key={s.name}>
                <Box sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: buzz > 0 ? `${sec.color}44` : 'divider', height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">{s.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.4 }}>
                      <Chip size="small" label={s.stage} sx={{ height: 16, fontSize: '0.55rem', bgcolor: `${sec.color}1a`, color: sec.color }} />
                      {buzz > 0 && <Chip size="small" label={`${buzz}`} icon={<Bolt sx={{ fontSize: '10px !important', color: '#FBBF24 !important' }} />}
                        sx={{ height: 16, fontSize: '0.55rem', bgcolor: '#F59E0B22', color: '#FBBF24' }} />}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.4, display: 'block', mt: 0.3 }}>{s.focus}</Typography>
                  {s.news_buzz?.latest_headline && buzz > 0 && (
                    <HeadlineLink headline={s.news_buzz.latest_headline.slice(0, 90) + '…'} accent={sec.color} />
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function IsraeliStartups() {
  const [founders, setFounders] = useState<any[]>([]);
  const [sectors, setSectors] = useState<any[]>([]);
  const [discovery, setDiscovery] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>('cybersecurity');

  const load = useCallback(async (forceRefresh = false) => {
    try {
      if (forceRefresh) { setRefreshing(true); await apiService.refreshIsraeliStartups(); }
      const [f, s, d] = await Promise.all([
        apiService.getIsraeliFounders(),
        apiService.getIsraeliSectors(),
        apiService.getIsraeliDiscovery(90, false),
      ]);
      setFounders(f.founders ?? []);
      setSectors(s.sectors ?? []);
      setDiscovery(d);
      setError(null);
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to load Israeli startups radar');
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  const reScan = useCallback(async () => {
    setScanning(true);
    try { setDiscovery(await apiService.getIsraeliDiscovery(90, true)); }
    catch (e: any) { setError('Re-scan failed: ' + (e.message || 'unknown')); }
    finally { setScanning(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, gap: 2 }}>
        <CircularProgress size={48} />
        <Typography color="text.secondary">Loading Israeli startups radar…</Typography>
      </Box>
    );
  }

  const totalFundingDeals = discovery?.funding_rounds?.length ?? 0;
  const totalStealth = discovery?.stealth_launches?.length ?? 0;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(135deg, #0a1530 0%, #102a5c 45%, #0a1530 100%)',
        border: '1px solid #ffffff18', borderRadius: 3, p: { xs: 2.5, md: 4 }, mb: 3, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, fontSize: 180, lineHeight: 1, pointerEvents: 'none' }}>🇮🇱</Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <RocketLaunch sx={{ color: ACCENT, fontSize: 28 }} />
              <Typography variant="h4" fontWeight="bold" color="white">Israeli Startups Radar</Typography>
            </Box>
            <Typography color="rgba(255,255,255,0.7)" sx={{ maxWidth: 720, lineHeight: 1.7 }}>
              A VC's early-warning system for the Startup Nation — fresh funding rounds, companies emerging from
              stealth, and exit-proven founders starting their next venture. Mined live from Israeli tech-news.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
              <Chip label={`${founders.length} founders tracked`} size="small" sx={{ bgcolor: '#ffffff22', color: 'white' }} />
              <Chip label={`${sectors.reduce((n, s) => n + s.startups.length, 0)} startups`} size="small" sx={{ bgcolor: '#ffffff22', color: 'white' }} />
              {totalFundingDeals > 0 && <Chip label={`${totalFundingDeals} live rounds`} size="small" sx={{ bgcolor: '#10B98133', color: '#34D399' }} />}
              {totalStealth > 0 && <Chip icon={<Visibility sx={{ fontSize: 13, color: '#A78BFA !important' }} />} label={`${totalStealth} out of stealth`} size="small" sx={{ bgcolor: '#7C3AED33', color: '#A78BFA' }} />}
            </Box>
          </Box>
          <Button variant="outlined" startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <Refresh />}
            onClick={() => load(true)} disabled={refreshing}
            sx={{ color: 'white', borderColor: '#ffffff44', '&:hover': { borderColor: 'white' }, whiteSpace: 'nowrap' }}>
            {refreshing ? 'Refreshing…' : 'Refresh feeds'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Live deal radar */}
      <DiscoverySection discovery={discovery} onRefresh={reScan} scanning={scanning} />

      {/* Founders to watch */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Star sx={{ color: '#FBBF24' }} /> Founders to Watch
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Exit-proven serial entrepreneurs. When they start something new — often in stealth — it's the earliest signal. Ranked by recent news activity.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {founders.map(f => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={f.name}><FounderCard f={f} /></Grid>
        ))}
      </Grid>

      {/* Sectors */}
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <RocketLaunch sx={{ color: ACCENT }} /> Emerging Startups by Sector
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Curated companies across Israel's strongest sectors, each enriched with live news buzz.
      </Typography>
      {sectors.map(sec => (
        <SectorAccordion key={sec.id} sec={sec} expanded={expanded === sec.id}
          onToggle={() => setExpanded(expanded === sec.id ? false : sec.id)} />
      ))}

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3 }}>
        News ingested every 3 hours from Israeli tech press. Founder profile links open LinkedIn/X search.
        Not investment advice — for deal sourcing and research only.
      </Typography>
    </Box>
  );
}
